import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, input, output } from '@angular/core';
import { GuardNoticeComponent } from '@workbuddy/ui/components/guard.notice';
import type { ContextGuardNoticeBand } from './context.guard.types';

// The context guard's own words, one voice across all three bands. The visual system (box,
// typography, band treatments, entrance) lives in the shared wb-guard-notice.
const CONTEXT_GUARD_COPY: Record<ContextGuardNoticeBand, { title: string; body: string }> = {
  advisory: { title: 'You’ve been talking here for a while', body: 'A new chat will feel fresher and use your allowance more efficiently.' },
  approaching: { title: 'This chat is getting a little long', body: 'Start a new chat soon so you don’t hit the conversation limit.' },
  blocked: { title: 'You’ve reached this chat’s limit', body: 'The conversation stays available to read and copy. Sending and uploading are paused here.' },
};

/**
 * Conversation-length notice for the agent composer stack: the shared wb-guard-notice dressed in
 * the context guard's copy.
 *
 * Purely presentational: the host decides WHICH band to show (and whether to show anything at all),
 * this component only renders it and reports the two things a user can do about it.
 *  - `advisory` and `approaching` sit above the composer, dismissible, composer keeps working.
 *  - `blocked` takes the composer's place; the host owns the actual input lock and keeps the
 *    read-only helper row beneath it. The dismiss affordance is withheld by the shared notice.
 *
 * The transcript above is untouched in every band: it stays readable, copyable and downloadable.
 */
@Component({
  selector: 'agent-context-guard-notice',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [GuardNoticeComponent],
  template: `<wb-guard-notice [band]="$band()" [title]="$copy().title" [body]="$copy().body" actionLabel="Start a new chat" [dismissible]="true" (action)="newChat.emit()" (dismiss)="dismiss.emit()" />`,
  styles: 'agent-context-guard-notice { display: block; }',
})
export class AgentContextGuardNoticeComponent {

  public readonly $band = input.required<ContextGuardNoticeBand>({ alias: 'band' });

  /** The user asked to start a fresh chat. The host owns the actual lifecycle. */
  public readonly newChat = output<void>();
  /** The user dismissed an advisory or approaching notice. Never emitted for `blocked`. */
  public readonly dismiss = output<void>();

  protected readonly $copy = computed(() => CONTEXT_GUARD_COPY[this.$band()]);
}
